import { AdmissionGroupReceiptType } from '../constants/admissionGroup'
import { BookingPaymentType } from '../constants/booking'
import { InventoryType } from '../constants/inventory'
import {
  BookingFeeType,
  FeeType,
  PaymentAmountType,
} from '../constants/pricing'
import type { BookingFee } from '../booking'
import {
  calculateBuyerTotal,
  numberToCurrency,
  type BuyerTotalOptions,
  type BuyerTotalResult,
} from './pricingUtils'

export const BALANCE_DUE_BALANCING_FEE_NAME = 'Taxes and fees on prior payment'

export type BalanceDueReceiptInput = {
  type?: string | null
  paymentType?: string | null
  price?: number | null
  subtotal?: number | null
  tax?: number | null
  tip?: number | null
  venueFeeTotal?: number | null
}

export type GetAdmissionGroupBalanceDueArgs = {
  minimumSpend: number
  receipts?: BalanceDueReceiptInput[] | null
  hasExistingPaymentLink: boolean
  taxPercent: number
  tipPercent: number
  fees?: BookingFee[] | null
  inventoryType?: InventoryType
  venue?: BuyerTotalOptions['venue'] | null
}

export type AdmissionGroupBalanceDue = {
  shouldShow: boolean
  buyerTotal: number
  amountPaid: number
  paidSubtotal: number
  remainingSubtotal: number
  balanceDue: number
  balancingFee: number
  quote?: BuyerTotalResult
}

const ZERO_PROCESSING_VENUE: NonNullable<BuyerTotalOptions['venue']> = {
  tableProcessingFee: 0,
  venueTableProcessingFee: 0,
  ticketProcessingFee: 0,
  venueTicketProcessingFee: 0,
}

export function isBalanceDueBalancingFee(fee: {
  name?: string | null
}): boolean {
  return fee.name === BALANCE_DUE_BALANCING_FEE_NAME
}

export function buildBalanceDueBalancingFee(amount: number): BookingFee {
  return {
    name: BALANCE_DUE_BALANCING_FEE_NAME,
    amount,
    type: FeeType.FLAT,
    taxable: false,
    feeType: BookingFeeType.OPTIONAL,
  }
}

export function withoutBalanceDueBalancingFee<
  T extends { name?: string | null },
>(fees?: T[] | null): T[] {
  return (fees ?? []).filter((fee) => !isBalanceDueBalancingFee(fee))
}

export function isBalanceDueDefaultAmount(
  paymentAmount: number,
  displayedBalanceDue: number
): boolean {
  return Math.abs(Number(paymentAmount || 0) - displayedBalanceDue) < 0.015
}

export function getBalanceDueChargeAmount(
  paymentAmountType: PaymentAmountType | null | undefined,
  paymentAmount: number,
  balanceDue?: Pick<
    AdmissionGroupBalanceDue,
    'balanceDue' | 'remainingSubtotal'
  > | null
): number {
  if (
    paymentAmountType === PaymentAmountType.BALANCE &&
    balanceDue &&
    isBalanceDueDefaultAmount(paymentAmount, balanceDue.balanceDue)
  ) {
    return balanceDue.remainingSubtotal
  }
  return paymentAmount
}

export function feesWithBalanceDueBalancer(
  fees: BookingFee[] | null | undefined,
  paymentAmountType: PaymentAmountType | null | undefined,
  paymentAmount: number,
  balanceDue?: Pick<
    AdmissionGroupBalanceDue,
    'balanceDue' | 'balancingFee'
  > | null
): BookingFee[] {
  const next = withoutBalanceDueBalancingFee(fees).map((fee) => ({ ...fee }))
  if (
    paymentAmountType === PaymentAmountType.BALANCE &&
    balanceDue &&
    isBalanceDueDefaultAmount(paymentAmount, balanceDue.balanceDue) &&
    balanceDue.balancingFee > 0
  ) {
    next.push(buildBalanceDueBalancingFee(balanceDue.balancingFee))
  }
  return next
}

function isPrepaidInternalReceipt(receipt: BalanceDueReceiptInput): boolean {
  if (receipt.type !== AdmissionGroupReceiptType.INTERNAL) return false
  return (
    receipt.paymentType === BookingPaymentType.ADVANCE ||
    receipt.paymentType === BookingPaymentType.ADVANCE_DEPOSIT
  )
}

function quoteExcludingProcessing(args: {
  price: number
  taxPercent: number
  tipPercent: number
  fees: BookingFee[]
  inventoryType: InventoryType
  venue?: BuyerTotalOptions['venue'] | null
}): BuyerTotalResult {
  return calculateBuyerTotal({
    paymentType: BookingPaymentType.ADVANCE,
    inventoryType: args.inventoryType,
    price: args.price,
    taxPercent: args.taxPercent || 0,
    tipPercent: args.tipPercent || 0,
    fees: args.fees,
    userPaysProcessing: false,
    venue: args.venue ?? ZERO_PROCESSING_VENUE,
  })
}

export function getAdmissionGroupBalanceDue(
  args: GetAdmissionGroupBalanceDueArgs
): AdmissionGroupBalanceDue {
  const minimumSpend = Number(args.minimumSpend) || 0
  const inventoryType = args.inventoryType ?? InventoryType.PACKAGE
  const fees = withoutBalanceDueBalancingFee(args.fees)

  let paidSubtotal = 0
  let amountPaid = 0
  for (const receipt of args.receipts ?? []) {
    if (!isPrepaidInternalReceipt(receipt)) continue
    const price = Number(receipt.price ?? receipt.subtotal ?? 0) || 0
    const tax = Number(receipt.tax ?? 0) || 0
    const tip = Number(receipt.tip ?? 0) || 0
    const venueFees = Number(receipt.venueFeeTotal ?? 0) || 0
    paidSubtotal += price
    amountPaid += price + tax + tip + venueFees
  }
  paidSubtotal = numberToCurrency(paidSubtotal)
  amountPaid = numberToCurrency(amountPaid)

  const fullQuote = quoteExcludingProcessing({
    price: minimumSpend,
    taxPercent: args.taxPercent,
    tipPercent: args.tipPercent,
    fees,
    inventoryType,
    venue: args.venue,
  })
  const buyerTotal = numberToCurrency(fullQuote.total)
  const remainingSubtotal = numberToCurrency(
    Math.max(0, minimumSpend - paidSubtotal)
  )
  const balanceDue = numberToCurrency(Math.max(0, buyerTotal - amountPaid))
  const remainingWithRates = numberToCurrency(
    quoteExcludingProcessing({
      price: remainingSubtotal,
      taxPercent: args.taxPercent,
      tipPercent: args.tipPercent,
      fees,
      inventoryType,
      venue: args.venue,
    }).total
  )
  const balancingFee = numberToCurrency(
    Math.max(0, balanceDue - remainingWithRates)
  )

  return {
    shouldShow:
      Boolean(args.hasExistingPaymentLink) && amountPaid > 0 && balanceDue > 0,
    buyerTotal,
    amountPaid,
    paidSubtotal,
    remainingSubtotal,
    balanceDue,
    balancingFee,
    quote: fullQuote,
  }
}
